import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, Globe } from "lucide-react";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { LANGS } from "@/i18n/strings";
import { scrollToId } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function LangSwitcher({ tone = "dark" }) {
  const { lang, setLang, t } = useLang();
  const cur = LANGS.find((l) => l.code === lang);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        data-testid="language-selector-dropdown"
        aria-label={t("language")}
        className={`flex items-center gap-2 px-2 py-2 text-xs tracking-[0.2em] transition-colors ${tone === "dark" ? "text-[#F7F3EB]/80 hover:text-[#C6A76A]" : "text-[#0B0B0C]/70 hover:text-[#0B0B0C]"}`}
      >
        <Globe className="h-4 w-4" strokeWidth={1.2} />
        <span className="font-latin">{cur?.short}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem] rounded-none border-[#C6A76A]/25 bg-[#0B0B0C] text-[#F7F3EB]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => setLang(l.code)}
            className={`cursor-pointer rounded-none py-2.5 focus:bg-[#241914] focus:text-[#C6A76A] ${l.code === lang ? "text-[#C6A76A]" : ""}`}
          >
            <span className="w-8 font-latin text-[0.65rem] tracking-[0.2em] opacity-60">{l.short}</span>
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NAV = [
  ["collection", "nav_collection"],
  ["house", "nav_house"],
  ["service", "nav_services"],
  ["contact", "contact_over"],
];

export function Header() {
  const { t, rtl } = useLang();
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [solid, setSolid] = useState(false);
  const { scrollY } = useScroll();
  const nav = useNavigate();
  const { pathname } = useLocation();
  useMotionValueEvent(scrollY, "change", (v) => setSolid(v > 40));

  const go = (id) => {
    setMenu(false);
    if (id === "contact") return nav("/contact");
    if (pathname !== "/") nav(`/#${id}`);
    else setTimeout(() => scrollToId(id), menu ? 300 : 0);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-40 border-b transition-[background-color,border-color] duration-500 ${solid ? "border-[#C6A76A]/15 bg-[#0B0B0C]/90 backdrop-blur-xl" : "border-transparent bg-[#0B0B0C]/40 backdrop-blur-md"}`}
      data-testid="site-header"
    >
      <div className="mx-auto grid h-[72px] max-w-[1440px] grid-cols-[1fr_auto_1fr] items-center gap-2 px-5 text-[#F7F3EB] sm:px-10">
        <div className="flex items-center gap-8">
          <button data-testid="mobile-menu-btn" onClick={() => setMenu(true)} className="lg:hidden" aria-label={t("menu")}>
            <Menu className="h-5 w-5" strokeWidth={1.2} />
          </button>
          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map(([id, key]) => (
              <button key={id} data-testid={`nav-${id}-link`} onClick={() => go(id)} className="link-line pb-1 text-[0.8rem] tracking-[0.18em] text-[#F7F3EB]/80 transition-colors hover:text-[#F7F3EB] rtl:tracking-normal rtl:text-sm">
                {t(key)}
              </button>
            ))}
          </nav>
        </div>
        <Link to="/" data-testid="nav-brand-logo" className="justify-self-center">
          <Logo />
        </Link>
        <div className="flex items-center justify-end gap-2 sm:gap-4">
          <LangSwitcher />
          <button data-testid="cart-drawer-trigger" onClick={() => setOpen(true)} className="group relative flex items-center gap-2 p-2" aria-label={t("bag")}>
            <ShoppingBag className="h-5 w-5 transition-transform duration-500 group-hover:-translate-y-0.5" strokeWidth={1.2} />
            <span className="hidden text-[0.8rem] tracking-[0.18em] sm:inline rtl:tracking-normal">{t("bag")}</span>
            <span data-testid="cart-count" className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#C6A76A] px-1 font-latin text-[0.65rem] font-medium text-[#0B0B0C]">
              {count}
            </span>
          </button>
        </div>
      </div>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side={rtl ? "right" : "left"} className="w-[85vw] border-[#C6A76A]/20 bg-[#0B0B0C] p-8 text-[#F7F3EB] sm:max-w-sm" data-lenis-prevent>
          <SheetTitle className="sr-only">{t("menu")}</SheetTitle>
          <Logo />
          <div className="mt-16 flex flex-col gap-7">
            {NAV.map(([id, key], i) => (
              <motion.button
                key={id}
                initial={{ opacity: 0, x: rtl ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                data-testid={`mobile-nav-${id}-link`}
                onClick={() => go(id)}
                className="font-display text-start text-4xl text-[#F7F3EB]"
              >
                {t(key)}
              </motion.button>
            ))}
          </div>
          <div className="gold-hair mt-16" />
        </SheetContent>
      </Sheet>
    </motion.header>
  );
}
